import { isMixedSchema, MixedSchema } from '@principle-theorem/editor';
import {
  AtLeast,
  DocumentReference,
  INamedDocument,
  INamedTypeDocument,
  isDocRef,
  isINamedDocument,
  isNamedTypeDocument,
  isObject,
  ISoftDelete,
  isTimestamp,
  Timestamp,
  TypeGuard,
  WithRef,
} from '@principle-theorem/shared';
import { isBoolean, isString } from 'lodash';
import {
  IClinicalNote,
  isClinicalNote,
} from '../clinical-charting/core/clinical-note';
import { IStatusHistory } from '../common';
import { IContact, IContactDetails } from '../contact/contact';
import { IMedia } from '../media/media';
import { IPatient, IPatientContactDetails } from '../patient/patient';
import { IPatientRelationship } from '../patient/patient-relationship';
import { IPractice } from '../practice/practice';
import { IStaffer } from '../staffer/staffer';
import { IBrand } from '../brand';
import { IInteractionV2, InteractionType } from '../..';
import { MentionResourceType } from '../mention/mention';

export enum ReferralCollection {
  Messages = 'referralMessages',
  Media = 'referralMedia',
  Interactions = 'referralInteractions',
  ClinicalNotes = 'referralClinicalNotes',
  Notes = 'referralNotes',
}

export type StafferActor = INamedTypeDocument<
  IStaffer,
  MentionResourceType.Staffer
>;

export type ContactActor = INamedTypeDocument<
  IContact,
  MentionResourceType.Contact
>;

export type ReferralActor = StafferActor | ContactActor;

export interface IReferral extends ISoftDelete {
  uid: string;
  status: ReferralStatus;
  statusHistory: IStatusHistory<ReferralStatus>[];
  type: ReferralType;
  referrer: ReferralActor;
  recipient: ReferralActor;
  patient: INamedDocument<IPatient>;
  participants: DocumentReference<IStaffer | IContact>[];
  unreadBy: DocumentReference<IStaffer | IContact>[];
  followers: DocumentReference<IStaffer>[];
  practice: INamedDocument<IPractice>;
  stafferDetails: IContactDetails;
  patientDetails: ReferralPatientDetails;
  urgency: ReferralUrgency;
  emailInvitationSent?: boolean;
  externalUserEmail: string;
  lastActivityBy?: ReferralActor;
  lastNotifiedAt?: Timestamp;
  patientLetter?: IReferralPatientLetter;
}

export interface IReferralMessage extends ISoftDelete {
  content: MixedSchema;
  actor: ReferralActor;
}

export interface IReferralMedia extends IMedia {
  actor: ReferralActor;
  patientMediaRef?: DocumentReference<IMedia>;
}

export interface IReferralClinicalNote extends ISoftDelete {
  clinicalNote: IClinicalNote;
  actor: ReferralActor;
}

export interface IReferralClinicalNotesChanged {
  added: WithRef<IClinicalNote>[];
  removed: WithRef<IReferralClinicalNote>[];
}

export interface IReferralNote extends ISoftDelete {
  content: MixedSchema;
  actor: INamedDocument<IStaffer>;
}

export interface IReferralInteraction
  extends Pick<IInteractionV2, 'content' | 'type' | 'deleted'> {
  actor: ReferralActor;
  metadata?: Record<string, string | number | boolean | undefined>;
}

export interface IReferralPatientLetter {
  content: MixedSchema;
  includeClinicalNotes?: boolean;
  selectedAttachmentRefs?: DocumentReference<IReferralMedia>[];
  selectedClinicalNoteRefs?: DocumentReference<IClinicalNote>[];
  headerTemplate?: MixedSchema;
  footerTemplate?: MixedSchema;
}

export type ReferralPatientDetails = OptionalPatientDetails &
  AtLeast<IPatient, 'name' | 'dateOfBirth'> &
  Partial<IPatientContactDetails> & {
    primaryContact?: IPatientRelationship;
  };

export type OptionalPatientDetails = {
  includePhone: boolean;
  includeEmail: boolean;
  includeAddress: boolean;
};

export type ReferralParticipant = WithRef<IStaffer> | WithRef<IContact>;

export enum ReferralUrgency {
  Routine = 'routine',
  Emergency = 'emergency',
}

export enum ReferralType {
  Outgoing = 'outgoing',
  Incoming = 'incoming',
}

export enum ReferralRole {
  Referrer = 'referrer',
  Recipient = 'recipient',
}

export enum ReferralStatus {
  Draft = 'draft',
  Sent = 'sent',
  Received = 'received',
  Completed = 'completed',
}

export enum ReferralEmailType {
  InviteExternalUser = 'inviteExternalUser',
  NotifyOfChanges = 'notifyOfChanges',
}

export const isStafferActor = TypeGuard.interface<StafferActor>({
  name: isString,
  ref: isDocRef,
  type: (type): type is MentionResourceType.Staffer =>
    type === MentionResourceType.Staffer,
});

export const isContactActor = TypeGuard.interface<ContactActor>({
  name: isString,
  ref: isDocRef,
  type: (type): type is MentionResourceType.Contact =>
    type === MentionResourceType.Contact,
});

export const isReferralPatientLetter =
  TypeGuard.interface<IReferralPatientLetter>({
    content: isMixedSchema,
    includeClinicalNotes: TypeGuard.nilOr(isBoolean),
    selectedAttachmentRefs: TypeGuard.nilOr(TypeGuard.arrayOf(isDocRef)),
    selectedClinicalNoteRefs: TypeGuard.nilOr(TypeGuard.arrayOf(isDocRef)),
    headerTemplate: TypeGuard.nilOr(isMixedSchema),
    footerTemplate: TypeGuard.nilOr(isMixedSchema),
  });

export const isReferral = TypeGuard.interface<IReferral>({
  uid: isString,
  status: TypeGuard.enumValue(ReferralStatus),
  statusHistory: TypeGuard.arrayOf(isObject),
  type: TypeGuard.enumValue(ReferralType),
  referrer: TypeGuard.nilOr(isNamedTypeDocument),
  recipient: TypeGuard.nilOr(isNamedTypeDocument),
  patient: TypeGuard.nilOr(isINamedDocument),
  participants: TypeGuard.nilOr(TypeGuard.arrayOf(isDocRef)),
  unreadBy: TypeGuard.nilOr(TypeGuard.arrayOf(isDocRef)),
  followers: TypeGuard.nilOr(TypeGuard.arrayOf(isDocRef)),
  externalUserEmail: TypeGuard.nilOr(isString),
  patientDetails: TypeGuard.nilOr(isObject),
  practice: TypeGuard.nilOr(isINamedDocument),
  stafferDetails: TypeGuard.nilOr(isObject),
  urgency: TypeGuard.nilOr(TypeGuard.enumValue(ReferralUrgency)),
  emailInvitationSent: TypeGuard.nilOr(isBoolean),
  lastActivityBy: TypeGuard.nilOr(isNamedTypeDocument),
  lastNotifiedAt: TypeGuard.nilOr(isTimestamp),
  patientLetter: TypeGuard.nilOr(isReferralPatientLetter),
  deleted: isBoolean,
});

export const isReferralMedia = TypeGuard.interface<IReferralMedia>({
  name: isString,
  path: isString,
  tags: TypeGuard.arrayOf(isINamedDocument),
  expiresAt: TypeGuard.nilOr(isTimestamp),
  metadata: TypeGuard.nilOr(isObject),
  actor: isNamedTypeDocument,
  patientMediaRef: TypeGuard.nilOr(isDocRef),
  md5Hash: TypeGuard.nilOr(isString),
  deleted: isBoolean,
});

export const isReferralClinicalNote =
  TypeGuard.interface<IReferralClinicalNote>({
    clinicalNote: isClinicalNote,
    actor: isNamedTypeDocument,
    deleted: isBoolean,
  });

export interface ISendReferralEmailRequest {
  brandRef: DocumentReference<IBrand>;
  referralRef: DocumentReference<IReferral>;
  subject: string;
  contentHtml: string;
  actor: ReferralActor;
}

export const isSendReferralEmailRequest =
  TypeGuard.interface<ISendReferralEmailRequest>({
    brandRef: isDocRef,
    referralRef: isDocRef,
    subject: isString,
    contentHtml: isString,
    actor: TypeGuard.isOneOf(isStafferActor, isContactActor),
  });

export interface ISendPatientReferralLetterRequest {
  brandRef: DocumentReference<IBrand>;
  referralRef: DocumentReference<IReferral>;
  subject: string;
  bodyHtml: string;
  emailBody?: string;
  headerHtml?: string;
  footerHtml?: string;
  attachmentRefs?: DocumentReference<IReferralMedia>[];
  includeClinicalNotes?: boolean;
  clinicalNotesHtml?: string;
}

export const isSendPatientReferralLetterRequest =
  TypeGuard.interface<ISendPatientReferralLetterRequest>({
    brandRef: isDocRef,
    referralRef: isDocRef,
    subject: isString,
    bodyHtml: isString,
    emailBody: TypeGuard.nilOr(isString),
    headerHtml: TypeGuard.nilOr(isString),
    footerHtml: TypeGuard.nilOr(isString),
    attachmentRefs: TypeGuard.nilOr(TypeGuard.arrayOf(isDocRef)),
    includeClinicalNotes: TypeGuard.nilOr(isBoolean),
    clinicalNotesHtml: TypeGuard.nilOr(isString),
  });

export const isReferralInteraction = TypeGuard.interface<IReferralInteraction>({
  type: TypeGuard.enumValue(InteractionType),
  content: isMixedSchema,
  actor: isNamedTypeDocument,
  metadata: TypeGuard.nilOr(isObject),
  deleted: isBoolean,
});

export const isReferralNote = TypeGuard.interface<IReferralNote>({
  content: isMixedSchema,
  actor: isINamedDocument,
  deleted: isBoolean,
});
